import React from "react";

export function TrackStatusSvg({
  status = "new",
  color = "#15C49A",
  orderType = "",
  order_type = "",
  completedText,
  completedLabel,
  className = "",
  style = {},
}) {
  const primaryColor = color || "#15C49A";

  const effectiveOrderType = String(
    orderType || order_type || "",
  ).toLowerCase();

  const isPickup =
    effectiveOrderType === "pickup" ||
    effectiveOrderType === "third_party_pickup" ||
    effectiveOrderType === "curbside_pickup";
  const isDineIn = effectiveOrderType === "dine_in";
  const isDelivery = effectiveOrderType === "delivery";

  const resolvedCompletedLabel = (
    completedLabel ||
    completedText ||
    (isDelivery
      ? "DELIVERED"
      : isDineIn
      ? "SERVED"
      : isPickup
      ? "PICKED UP"
      : "COMPLETED")
  ).toUpperCase();

  const badgeWidth = Math.max(
    80,
    Math.round(resolvedCompletedLabel.length * 8.5 + 24),
  );
  const badgeX = 200 - badgeWidth / 2;

  switch (status) {
    case "accepted":
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 400 260"
          className={className}
          style={{ width: "100%", height: "100%", maxHeight: "100%", ...style }}
          fill="none"
        >
          <style>
            {`
              @keyframes steamRise1 {
                0% { transform: translateY(0) scaleY(1); opacity: 0.2; }
                50% { opacity: 0.8; }
                100% { transform: translateY(-16px) scaleY(1.2); opacity: 0; }
              }
              @keyframes steamRise2 {
                0% { transform: translateY(0) scaleY(1); opacity: 0.2; }
                50% { opacity: 0.9; }
                100% { transform: translateY(-20px) scaleY(1.3); opacity: 0; }
              }
              @keyframes potSimmer {
                0%, 100% { transform: translateY(0); }
                50% { transform: translateY(-2px); }
              }
              .steam-1 { animation: steamRise1 1.8s infinite ease-out; }
              .steam-2 { animation: steamRise2 2.2s 0.5s infinite ease-out; }
              .steam-3 { animation: steamRise1 1.9s 0.9s infinite ease-out; }
              .pot-group { animation: potSimmer 2s infinite ease-in-out; }
            `}
          </style>
          <defs>
            <radialGradient id="accepted-glow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor={primaryColor} stopOpacity="0.25" />
              <stop offset="100%" stopColor={primaryColor} stopOpacity="0" />
            </radialGradient>
            <linearGradient
              id="accepted-pot"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="100%"
            >
              <stop offset="0%" stopColor={primaryColor} />
              <stop offset="100%" stopColor={primaryColor} stopOpacity="0.8" />
            </linearGradient>
          </defs>

          {/* Background Glow */}
          <circle cx="200" cy="140" r="110" fill="url(#accepted-glow)" />

          {/* Ground shadow */}
          <ellipse cx="200" cy="225" rx="100" ry="12" fill="#E2E8F0" />
          <ellipse cx="200" cy="225" rx="60" ry="7" fill="#CBD5E1" />

          {/* Animated Steam wisps */}
          <path
            className="steam-1"
            d="M170 85 C 165 70, 175 60, 170 45"
            stroke={primaryColor}
            strokeWidth="4"
            strokeLinecap="round"
            fill="none"
          />
          <path
            className="steam-2"
            d="M200 80 C 195 62, 205 52, 200 35"
            stroke={primaryColor}
            strokeWidth="5"
            strokeLinecap="round"
            fill="none"
          />
          <path
            className="steam-3"
            d="M230 85 C 225 70, 235 60, 230 45"
            stroke={primaryColor}
            strokeWidth="4"
            strokeLinecap="round"
            fill="none"
          />

          <g className="pot-group">
            {/* Chef Hat on Pot */}
            <path
              d="M170 95 C 160 85, 170 65, 185 70 C 195 55, 215 55, 220 70 C 235 65, 245 85, 235 95 Z"
              fill="#FFFFFF"
              stroke="#CBD5E1"
              strokeWidth="3"
            />
            <rect x="175" y="92" width="55" height="10" rx="3" fill="#F1F5F9" />

            {/* Pot Handles */}
            <rect x="95" y="135" width="25" height="12" rx="6" fill="#64748B" />
            <rect
              x="280"
              y="135"
              width="25"
              height="12"
              rx="6"
              fill="#64748B"
            />

            {/* Main Pot Body */}
            <path
              d="M110 120 L 290 120 L 275 210 C 273 218, 265 224, 255 224 L 145 224 C 135 224, 127 218, 125 210 Z"
              fill="url(#accepted-pot)"
            />

            {/* Pot Rim */}
            <rect
              x="105"
              y="112"
              width="190"
              height="14"
              rx="7"
              fill={primaryColor}
            />
            <rect
              x="115"
              y="115"
              width="170"
              height="4"
              rx="2"
              fill="#FFFFFF"
              opacity="0.4"
            />

            {/* Sparkles / Highlights */}
            <circle cx="140" cy="160" r="10" fill="#FFFFFF" opacity="0.3" />
            <circle cx="170" cy="180" r="7" fill="#FFFFFF" opacity="0.25" />
            <circle cx="245" cy="155" r="12" fill="#FFFFFF" opacity="0.2" />

            {/* Spatula / Cooking Tool */}
            <path d="M260 90 L 290 50 L 302 58 L 272 98 Z" fill="#94A3B8" />
            <rect
              x="285"
              y="42"
              width="25"
              height="18"
              rx="3"
              transform="rotate(35 285 42)"
              fill={primaryColor}
            />
          </g>
        </svg>
      );

    case "ready":
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 400 260"
          className={className}
          style={{ width: "100%", height: "100%", maxHeight: "100%", ...style }}
          fill="none"
        >
          <style>
            {`
              @keyframes readyGlow {
                0%, 100% { transform: scale(1); opacity: 0.8; }
                50% { transform: scale(1.08); opacity: 1; }
              }
              .ready-badge {
                animation: readyGlow 2s infinite ease-in-out;
                transform-origin: 200px 180px;
              }
            `}
          </style>
          <defs>
            <radialGradient id="ready-glow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor={primaryColor} stopOpacity="0.28" />
              <stop offset="100%" stopColor={primaryColor} stopOpacity="0" />
            </radialGradient>
          </defs>

          {/* Background Glow */}
          <circle cx="200" cy="130" r="110" fill="url(#ready-glow)" />

          {/* Ground shadow */}
          <ellipse cx="200" cy="225" rx="110" ry="12" fill="#E2E8F0" />

          {/* Storefront / Stand */}
          <rect
            x="110"
            y="110"
            width="180"
            height="110"
            rx="8"
            fill="#FFFFFF"
            stroke="#E2E8F0"
            strokeWidth="4"
          />
          <rect
            x="135"
            y="145"
            width="130"
            height="75"
            rx="6"
            fill="#F8FAFC"
            stroke="#CBD5E1"
            strokeWidth="2"
          />

          {/* Counter table */}
          <rect x="100" y="135" width="200" height="12" rx="6" fill="#475569" />

          {/* Awning Stripes */}
          <path d="M90 100 L 310 100 L 295 70 L 105 70 Z" fill={primaryColor} />
          {/* Awning valance scallops */}
          <path
            d="M90 100 Q 105 115 120 100 Q 135 115 150 100 Q 165 115 180 100 Q 195 115 210 100 Q 225 115 240 100 Q 255 115 270 100 Q 285 115 300 100 Q 305 110 310 100 L 310 95 L 90 95 Z"
            fill={primaryColor}
          />
          {/* White stripes on awning */}
          <path
            d="M125 70 L 140 100 L 160 100 L 145 70 Z"
            fill="#FFFFFF"
            opacity="0.9"
          />
          <path
            d="M185 70 L 195 100 L 215 100 L 205 70 Z"
            fill="#FFFFFF"
            opacity="0.9"
          />
          <path
            d="M245 70 L 250 100 L 270 100 L 265 70 Z"
            fill="#FFFFFF"
            opacity="0.9"
          />

          {/* Shopping Bag on Counter */}
          <rect
            x="175"
            y="150"
            width="50"
            height="60"
            rx="6"
            fill={primaryColor}
          />
          <path
            d="M187 150 C 187 140, 213 140, 213 150"
            stroke="#FFFFFF"
            strokeWidth="3"
            strokeLinecap="round"
            fill="none"
          />

          {/* Checkmark Badge on Bag */}
          <g className="ready-badge">
            <circle cx="200" cy="180" r="14" fill="#FFFFFF" />
            <path
              d="M194 180 L 198 184 L 206 176"
              stroke={primaryColor}
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>

          {/* Sparkles */}
          <path
            d="M130 50 L 133 58 L 141 61 L 133 64 L 130 72 L 127 64 L 119 61 L 127 58 Z"
            fill={primaryColor}
          />
          <path
            d="M280 45 L 282 51 L 288 53 L 282 55 L 280 61 L 278 55 L 272 53 L 278 51 Z"
            fill={primaryColor}
            opacity="0.8"
          />
        </svg>
      );

    case "dispatched":
    case "soon":
    case "out_for_delivery":
    case "picked_up":
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 400 260"
          className={className}
          style={{ width: "100%", height: "100%", maxHeight: "100%", ...style }}
          fill="none"
        >
          <style>
            {`
              @keyframes dispatchBob {
                0%, 100% { transform: translateY(0); }
                50% { transform: translateY(-6px); }
              }
              @keyframes routeFlow {
                0% { stroke-dashoffset: 24; }
                100% { stroke-dashoffset: 0; }
              }
              @keyframes pinFloat {
                0%, 100% { transform: translateY(0); }
                50% { transform: translateY(-7px); }
              }
              @keyframes pinPulse {
                0%   { transform: scale(1);   opacity: 0.6; }
                100% { transform: scale(1.9); opacity: 0; }
              }
              @keyframes speedLineSlide {
                0%   { transform: translateX(30px); opacity: 0; }
                20%  { opacity: 1; }
                80%  { opacity: 0.85; }
                100% { transform: translateX(-40px); opacity: 0; }
              }
              @keyframes sparkleGlint {
                0%, 100% { transform: scale(1) rotate(0deg); opacity: 0.7; }
                50% { transform: scale(1.25) rotate(15deg); opacity: 1; }
              }

              .dispatch-items-group {
                animation: dispatchBob 2.2s infinite ease-in-out;
              }
              .dispatch-route {
                animation: routeFlow 1s infinite linear;
              }
              .dispatch-pin {
                animation: pinFloat 2s infinite ease-in-out;
              }
              .dispatch-pin-pulse {
                animation: pinPulse 1.8s infinite ease-out;
                transform-origin: 320px 115px;
              }
              .speed-line-1 { animation: speedLineSlide 1s infinite linear; }
              .speed-line-2 { animation: speedLineSlide 1.2s 0.2s infinite linear; }
              .speed-line-3 { animation: speedLineSlide 0.9s 0.4s infinite linear; }
              .sparkle-star { animation: sparkleGlint 2s infinite ease-in-out; transform-origin: center; transform-box: fill-box; }
            `}
          </style>

          <defs>
            <radialGradient id="dispatch-glow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor={primaryColor} stopOpacity="0.22" />
              <stop offset="100%" stopColor={primaryColor} stopOpacity="0" />
            </radialGradient>
            <linearGradient
              id="dispatch-bag-grad"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="100%"
            >
              <stop offset="0%" stopColor={primaryColor} />
              <stop offset="100%" stopColor={primaryColor} stopOpacity="0.85" />
            </linearGradient>
            <linearGradient
              id="dispatch-box-grad"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="100%"
            >
              <stop offset="0%" stopColor="#FFFFFF" />
              <stop offset="100%" stopColor="#F8FAFC" />
            </linearGradient>
          </defs>

          {/* ── Background Glow ── */}
          <circle cx="200" cy="130" r="110" fill="url(#dispatch-glow)" />

          {/* ── Ground Shadows ── */}
          <ellipse cx="200" cy="225" rx="120" ry="12" fill="#E2E8F0" />
          <ellipse
            cx="195"
            cy="225"
            rx="75"
            ry="7"
            fill="#CBD5E1"
            opacity="0.6"
          />

          {/* ── Animated Route Trajectory (Curving to Destination Pin) ── */}
          <path
            className="dispatch-route"
            d="M 60 215 Q 160 180, 240 210 T 320 135"
            stroke={primaryColor}
            strokeWidth="3.5"
            strokeLinecap="round"
            strokeDasharray="8 6"
            fill="none"
            opacity="0.45"
          />

          {/* ── Origin Node Dot ── */}
          <circle
            cx="60"
            cy="215"
            r="7"
            fill="#E2E8F0"
            stroke="#94A3B8"
            strokeWidth="2"
          />
          <circle cx="60" cy="215" r="3.5" fill={primaryColor} />

          {/* ── Speed / Motion Breeze Lines on Left ── */}
          <line
            className="speed-line-1"
            x1="60"
            y1="130"
            x2="105"
            y2="130"
            stroke={primaryColor}
            strokeWidth="3.5"
            strokeLinecap="round"
            opacity="0.75"
          />
          <line
            className="speed-line-2"
            x1="40"
            y1="155"
            x2="95"
            y2="155"
            stroke={primaryColor}
            strokeWidth="4"
            strokeLinecap="round"
            opacity="0.9"
          />
          <line
            className="speed-line-3"
            x1="70"
            y1="180"
            x2="115"
            y2="180"
            stroke={primaryColor}
            strokeWidth="3"
            strokeLinecap="round"
            opacity="0.6"
          />

          {/* Sparkles / Dynamic Energy */}
          <path
            className="sparkle-star"
            d="M 135 90 L 137.5 97 L 144.5 99.5 L 137.5 102 L 135 109 L 132.5 102 L 125.5 99.5 L 132.5 97 Z"
            fill={primaryColor}
            opacity="0.75"
          />
          <path
            className="sparkle-star"
            d="M 285 100 L 287 105 L 292 107 L 287 109 L 285 114 L 283 109 L 278 107 L 283 105 Z"
            fill={primaryColor}
            opacity="0.65"
          />

          {/* ═════════════════════════════════════════════════════
                ORDER IN TRANSIT (GENERIC FOR DELIVERY & PICKUP)
              ═════════════════════════════════════════════════════ */}
          <g className="dispatch-items-group">
            {/* ── Takeaway / Pickup Bag (Back/Left) ── */}
            {/* Bag Handles */}
            <path
              d="M 152 114 C 152 94, 180 94, 180 114"
              stroke={primaryColor}
              strokeWidth="4"
              strokeLinecap="round"
              fill="none"
            />
            {/* Bag Main Body */}
            <rect
              x="138"
              y="114"
              width="58"
              height="74"
              rx="6"
              fill="url(#dispatch-bag-grad)"
            />
            {/* Bag Fold / Highlight */}
            <path
              d="M 143 122 L 167 182 L 191 122"
              stroke="#FFFFFF"
              strokeWidth="2.5"
              strokeOpacity="0.22"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {/* Bag Tag / Receipt */}
            <rect
              x="152"
              y="132"
              width="30"
              height="34"
              rx="3"
              fill="#FFFFFF"
              opacity="0.95"
            />
            <line
              x1="158"
              y1="141"
              x2="176"
              y2="141"
              stroke="#94A3B8"
              strokeWidth="2.5"
              strokeLinecap="round"
            />
            <line
              x1="158"
              y1="148"
              x2="172"
              y2="148"
              stroke="#CBD5E1"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <line
              x1="158"
              y1="155"
              x2="168"
              y2="155"
              stroke={primaryColor}
              strokeWidth="2"
              strokeLinecap="round"
            />

            {/* ── Order Box / Package (Front/Right) ── */}
            {/* Box Body */}
            <rect
              x="180"
              y="136"
              width="76"
              height="58"
              rx="6"
              fill="url(#dispatch-box-grad)"
              stroke="#E2E8F0"
              strokeWidth="2.5"
            />
            {/* Box Bottom Depth Shadow */}
            <path
              d="M 181 184 L 255 184 L 255 188 C 255 191, 252 193, 249 193 L 187 193 C 184 193, 181 191, 181 188 Z"
              fill="#E2E8F0"
              opacity="0.65"
            />
            {/* Horizontal Brand Ribbon */}
            <rect
              x="180"
              y="160"
              width="76"
              height="11"
              fill={primaryColor}
              opacity="0.3"
            />
            {/* Vertical Brand Ribbon */}
            <rect
              x="210"
              y="136"
              width="16"
              height="58"
              fill={primaryColor}
              opacity="0.9"
            />
            {/* Parcel Stamp / Label */}
            <rect
              x="188"
              y="144"
              width="16"
              height="12"
              rx="2"
              fill="#FFFFFF"
              stroke="#CBD5E1"
              strokeWidth="1.2"
            />
            <line
              x1="191"
              y1="148"
              x2="201"
              y2="148"
              stroke="#94A3B8"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
            <line
              x1="191"
              y1="152"
              x2="198"
              y2="152"
              stroke={primaryColor}
              strokeWidth="1.5"
              strokeLinecap="round"
            />

            {/* ── In-Transit Chevrons Badge (>> Fast / On the way) ── */}
            <g transform="translate(236, 114)">
              <circle
                cx="16"
                cy="16"
                r="16"
                fill="#FFFFFF"
                stroke="#E2E8F0"
                strokeWidth="2"
              />
              <path
                d="M 10 10 L 16 16 L 10 22"
                stroke={primaryColor}
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
              <path
                d="M 16 10 L 22 16 L 16 22"
                stroke={primaryColor}
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
                opacity="0.5"
              />
            </g>
          </g>

          {/* ── Destination Location Pin (floating + pulse) ── */}
          <g className="dispatch-pin">
            {/* Pin Ground Target Shadow */}
            <ellipse
              cx="320"
              cy="142"
              rx="11"
              ry="4"
              fill="#CBD5E1"
              opacity="0.7"
            />
            {/* Pulse Wave */}
            <circle
              className="dispatch-pin-pulse"
              cx="320"
              cy="115"
              r="14"
              fill={primaryColor}
              opacity="0.35"
            />
            {/* Pin Map Marker */}
            <path
              d="M 320 95 C 310 95, 302 103, 302 113 C 302 127, 320 142, 320 142 C 320 142, 338 127, 338 113 C 338 103, 330 95, 320 95 Z"
              fill={primaryColor}
            />
            {/* White Pin Core Dot */}
            <circle cx="320" cy="111" r="5.5" fill="#FFFFFF" />
          </g>
        </svg>
      );

    case "completed":
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 400 260"
          className={className}
          style={{ width: "100%", height: "100%", maxHeight: "100%", ...style }}
          fill="none"
        >
          <style>
            {`
              @keyframes deliveredBounce {
                0%, 100% { transform: translateY(0); }
                50% { transform: translateY(-4px); }
              }
              @keyframes badgeSuccessPulse {
                0%, 100% { transform: scale(1); }
                50% { transform: scale(1.08); }
              }
              @keyframes confettiFloat {
                0%, 100% { transform: translateY(0) rotate(0deg); }
                50% { transform: translateY(-6px) rotate(8deg); }
              }
              @keyframes sparkleGlint {
                0%, 100% { transform: scale(1) rotate(0deg); opacity: 0.7; }
                50% { transform: scale(1.25) rotate(15deg); opacity: 1; }
              }
              .delivered-group { animation: deliveredBounce 2.5s infinite ease-in-out; }
              .success-badge { animation: badgeSuccessPulse 2s infinite ease-in-out; transform-origin: 200px 105px; }
              .confetti-group { animation: confettiFloat 3s infinite ease-in-out; }
              .sparkle-star { animation: sparkleGlint 2s infinite ease-in-out; transform-origin: center; transform-box: fill-box; }
            `}
          </style>
          <defs>
            <radialGradient id="completed-glow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor={primaryColor} stopOpacity="0.28" />
              <stop offset="100%" stopColor={primaryColor} stopOpacity="0" />
            </radialGradient>
            <linearGradient
              id="parcel-grad"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="100%"
            >
              <stop offset="0%" stopColor={primaryColor} />
              <stop offset="100%" stopColor={primaryColor} stopOpacity="0.85" />
            </linearGradient>
            <linearGradient id="badge-grad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={primaryColor} />
              <stop offset="100%" stopColor={primaryColor} stopOpacity="0.9" />
            </linearGradient>
          </defs>

          {/* Background Glow */}
          <circle cx="200" cy="135" r="115" fill="url(#completed-glow)" />

          {/* Ground shadow */}
          <ellipse cx="200" cy="225" rx="100" ry="11" fill="#E2E8F0" />
          <ellipse cx="200" cy="225" rx="65" ry="6" fill="#CBD5E1" />

          {/* Confetti & Streamers */}
          <g className="confetti-group">
            <circle cx="85" cy="75" r="6" fill={primaryColor} />
            <circle cx="315" cy="70" r="7" fill="#F59E0B" />
            <circle cx="105" cy="155" r="5" fill="#3B82F6" />
            <circle cx="300" cy="160" r="6" fill={primaryColor} opacity="0.8" />
            <rect
              x="75"
              y="115"
              width="12"
              height="6"
              rx="2"
              transform="rotate(30 75 115)"
              fill="#EC4899"
            />
            <rect
              x="320"
              y="110"
              width="14"
              height="6"
              rx="2"
              transform="rotate(-25 320 110)"
              fill="#10B981"
            />
            <rect
              x="135"
              y="45"
              width="10"
              height="5"
              rx="2"
              transform="rotate(45 135 45)"
              fill={primaryColor}
            />
            <rect
              x="265"
              y="40"
              width="12"
              height="5"
              rx="2"
              transform="rotate(-40 265 40)"
              fill="#F59E0B"
            />

            {/* Sparkles */}
            <path
              className="sparkle-star"
              d="M95 40 L 98 48 L 106 51 L 98 54 L 95 62 L 92 54 L 84 51 L 92 48 Z"
              fill="#F59E0B"
            />
            <path
              className="sparkle-star"
              d="M305 40 L 307 46 L 313 48 L 307 50 L 305 56 L 303 50 L 297 48 L 303 46 Z"
              fill={primaryColor}
            />
            <path
              className="sparkle-star"
              d="M285 190 L 287 195 L 292 197 L 287 199 L 285 204 L 283 199 L 278 197 L 283 195 Z"
              fill={primaryColor}
              opacity="0.8"
            />
          </g>

          {/* Delivered Package Group */}
          <g className="delivered-group">
            {/* Main Parcel Box */}
            <rect
              x="135"
              y="120"
              width="130"
              height="95"
              rx="12"
              fill="url(#parcel-grad)"
              stroke="#FFFFFF"
              strokeWidth="3"
            />

            {/* Box Highlight top lid */}
            <path
              d="M135 132 L 265 132"
              stroke="#FFFFFF"
              strokeWidth="2.5"
              strokeLinecap="round"
              opacity="0.4"
            />

            {/* Vertical Ribbon Wrap */}
            <rect
              x="190"
              y="120"
              width="20"
              height="95"
              fill="#FFFFFF"
              opacity="0.3"
            />

            {/* Horizontal Ribbon Wrap */}
            <rect
              x="135"
              y="160"
              width="130"
              height="15"
              fill="#FFFFFF"
              opacity="0.25"
            />

            {/* Shipping / Order Label on Box */}
            <rect
              x="148"
              y="142"
              width="34"
              height="26"
              rx="4"
              fill="#FFFFFF"
            />
            {isPickup ? (
              <>
                <path
                  d="M161 146 C 161 143, 169 143, 169 146"
                  stroke={primaryColor}
                  strokeWidth="1.5"
                  fill="none"
                  strokeLinecap="round"
                />
                <rect
                  x="157"
                  y="146"
                  width="16"
                  height="14"
                  rx="2"
                  fill={primaryColor}
                  opacity="0.18"
                />
                <line
                  x1="153"
                  y1="163"
                  x2="177"
                  y2="163"
                  stroke="#CBD5E1"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </>
            ) : (
              <>
                <rect
                  x="153"
                  y="147"
                  width="24"
                  height="2.5"
                  rx="1"
                  fill="#94A3B8"
                />
                <rect
                  x="153"
                  y="152"
                  width="18"
                  height="2.5"
                  rx="1"
                  fill="#CBD5E1"
                />
                <rect
                  x="153"
                  y="157"
                  width="20"
                  height="2"
                  rx="1"
                  fill={primaryColor}
                />
              </>
            )}

            {/* Success Checkmark Shield Badge Floating on Top of Box */}
            <g className="success-badge">
              <circle
                cx="200"
                cy="105"
                r="36"
                fill="url(#badge-grad)"
                stroke="#FFFFFF"
                strokeWidth="5"
              />
              <circle
                cx="200"
                cy="105"
                r="29"
                fill="none"
                stroke="#FFFFFF"
                strokeWidth="1.5"
                strokeDasharray="4 3"
                opacity="0.6"
              />
              <path
                d="M188 105 L 196 113 L 213 96"
                stroke="#FFFFFF"
                strokeWidth="5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </g>

            {/* Completed Status Pill Badge */}
            <rect
              x={badgeX}
              y="52"
              width={badgeWidth}
              height="24"
              rx="12"
              fill="#FFFFFF"
              stroke={primaryColor}
              strokeWidth="2"
            />
            <text
              x="200"
              y="68"
              textAnchor="middle"
              fill={primaryColor}
              fontSize="10"
              fontWeight="800"
              letterSpacing="1"
              fontFamily="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
            >
              {resolvedCompletedLabel}
            </text>
          </g>
        </svg>
      );

    case "cancelled":
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 400 260"
          className={className}
          style={{ width: "100%", height: "100%", maxHeight: "100%", ...style }}
          fill="none"
        >
          <style>
            {`
              @keyframes cancelPulse {
                0%, 100% { transform: scale(1); }
                50% { transform: scale(1.06); }
              }
              .cancel-cross { animation: cancelPulse 2.5s infinite ease-in-out; transform-origin: 200px 170px; }
            `}
          </style>
          <defs>
            <radialGradient id="cancelled-glow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#EF4444" stopOpacity="0.2" />
              <stop offset="100%" stopColor="#EF4444" stopOpacity="0" />
            </radialGradient>
          </defs>

          {/* Background Glow */}
          <circle cx="200" cy="130" r="110" fill="url(#cancelled-glow)" />

          {/* Ground shadow */}
          <ellipse cx="200" cy="225" rx="80" ry="10" fill="#E2E8F0" />

          {/* Cancelled Box / Document */}
          <rect
            x="135"
            y="65"
            width="130"
            height="150"
            rx="12"
            fill="#FFFFFF"
            stroke="#E2E8F0"
            strokeWidth="4"
          />
          <rect x="155" y="95" width="90" height="8" rx="4" fill="#E2E8F0" />
          <rect x="155" y="115" width="70" height="8" rx="4" fill="#E2E8F0" />
          <rect x="155" y="135" width="80" height="8" rx="4" fill="#E2E8F0" />

          {/* Red Alert Cross Badge */}
          <g className="cancel-cross">
            <circle
              cx="200"
              cy="170"
              r="32"
              fill="#EF4444"
              stroke="#FFFFFF"
              strokeWidth="4"
            />
            <path
              d="M189 159 L 211 181 M 211 159 L 189 181"
              stroke="#FFFFFF"
              strokeWidth="5"
              strokeLinecap="round"
            />
          </g>
        </svg>
      );

    case "new":
    default:
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 400 260"
          className={className}
          style={{ width: "100%", height: "100%", maxHeight: "100%", ...style }}
          fill="none"
        >
          <style>
            {`
              @keyframes bagFloat {
                0%, 100% { transform: translateY(0); }
                50% { transform: translateY(-3px); }
              }
              @keyframes sparkleGlint {
                0%, 100% { transform: scale(1) rotate(0deg); opacity: 0.7; }
                50% { transform: scale(1.25) rotate(15deg); opacity: 1; }
              }
              .new-bag { animation: bagFloat 2s infinite ease-in-out; }
              .sparkle-star { animation: sparkleGlint 2s infinite ease-in-out; transform-origin: center; transform-box: fill-box; }
            `}
          </style>
          <defs>
            <radialGradient id="new-glow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor={primaryColor} stopOpacity="0.22" />
              <stop offset="100%" stopColor={primaryColor} stopOpacity="0" />
            </radialGradient>
            <linearGradient id="bag-grad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={primaryColor} />
              <stop offset="100%" stopColor={primaryColor} stopOpacity="0.85" />
            </linearGradient>
          </defs>

          {/* Background Glow */}
          <circle cx="200" cy="130" r="110" fill="url(#new-glow)" />

          {/* Ground shadow */}
          <ellipse cx="200" cy="225" rx="90" ry="10" fill="#E2E8F0" />

          {/* Receipt behind bag */}
          <rect
            x="200"
            y="60"
            width="75"
            height="110"
            rx="8"
            fill="#FFFFFF"
            stroke="#E2E8F0"
            strokeWidth="3"
          />
          <rect x="215" y="80" width="45" height="6" rx="3" fill="#CBD5E1" />
          <rect x="215" y="95" width="35" height="6" rx="3" fill="#CBD5E1" />
          <rect x="215" y="110" width="40" height="6" rx="3" fill="#CBD5E1" />
          <circle cx="237" cy="140" r="12" fill={primaryColor} opacity="0.3" />

          {/* Shopping Bag in Theme Color */}
          <g className="new-bag">
            <rect
              x="130"
              y="100"
              width="105"
              height="115"
              rx="12"
              fill="url(#bag-grad)"
            />
            {/* Handle */}
            <path
              d="M158 100 C 158 75, 207 75, 207 100"
              stroke="#FFFFFF"
              strokeWidth="6"
              strokeLinecap="round"
              fill="none"
            />
            {/* Bag Fold / Highlight line */}
            <path
              d="M142 120 L 223 120"
              stroke="#FFFFFF"
              strokeWidth="2"
              strokeLinecap="round"
              opacity="0.3"
            />

            {/* Checkmark inside Bag */}
            <circle cx="182" cy="155" r="22" fill="#FFFFFF" />
            <path
              d="M172 155 L 179 162 L 194 147"
              stroke={primaryColor}
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>

          {/* Floating Sparkles */}
          <path
            className="sparkle-star"
            d="M105 70 L 108 77 L 115 80 L 108 83 L 105 90 L 102 83 L 95 80 L 102 77 Z"
            fill={primaryColor}
          />
          <path
            className="sparkle-star"
            d="M285 60 L 287 65 L 292 67 L 287 69 L 285 74 L 283 69 L 278 67 L 283 65 Z"
            fill={primaryColor}
            opacity="0.8"
          />
          <circle cx="115" cy="140" r="4" fill={primaryColor} opacity="0.6" />
          <circle cx="270" cy="170" r="5" fill={primaryColor} opacity="0.7" />
        </svg>
      );
  }
}
